import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { SocialAuthButton } from '@/components/auth/SocialAuthButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { PortalBranding, getPortalConfig, type PortalType } from '@/components/auth/PortalBranding';
import { Loader2, Heart, AlertCircle, ArrowLeft } from 'lucide-react';

export default function Login() {
  const [searchParams] = useSearchParams();
  const portal = (searchParams.get('portal') as PortalType) || 'patient';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { signIn, user, isLoading: authLoading, isAdmin, isDoctor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
  const portalConfig = getPortalConfig(portal);

  // Redirect once auth state resolves (including roles) — handles email/password AND Google OAuth.
  // isLoading stays true until roles are fetched, so isAdmin/isDoctor are final here.
  useEffect(() => {
    if (authLoading || !user) return;
    if (isAdmin) { navigate('/admin', { replace: true }); return; }
    if (isDoctor) { navigate('/consultation', { replace: true }); return; }
    navigate(from === '/' ? '/dashboard' : from, { replace: true });
  }, [user, authLoading, isAdmin, isDoctor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        // Make Supabase error messages user-friendly
        const msg = error.message;
        if (msg.includes('Invalid login credentials')) {
          setError('Incorrect email or password. Please try again.');
        } else if (msg.includes('Email not confirmed')) {
          setError('Please confirm your email address first. Check your inbox for a verification link.');
        } else if (msg.includes('Too many requests')) {
          setError('Too many login attempts. Please wait a few minutes and try again.');
        } else {
          setError(msg);
        }
      }
      // On success: do NOT navigate here — the useEffect above handles it
      // once AuthContext finishes loading roles (isLoading goes false)
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float-delayed" />
      
      <motion.div 
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to home
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <motion.div 
            className="relative"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Heart className="h-10 w-10 text-primary" />
            <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full" />
          </motion.div>
          <span className="text-2xl font-bold text-foreground">
            Apne<span className="text-primary">Doctors</span>
          </span>
        </div>

        <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Gradient Top Border */}
          <div className="h-1 bg-gradient-to-r from-primary via-secondary to-primary" />
          
          <CardHeader className="space-y-4 pt-8">
            <PortalBranding portal={portal} />
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 px-6">
              {/* Error Alert */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {/* Google Sign In */}
              <SocialAuthButton provider="google" disabled={isLoading} />
              
              <AuthDivider />
              
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20 transition-all"
                />
              </div>
              
              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20"
                />
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col gap-4 px-6 pb-8">
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold relative overflow-hidden group"
                disabled={isLoading}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/20 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  portalConfig.cta
                )}
              </Button>
              
              <p className="text-sm text-muted-foreground text-center">
                Don't have an account?{' '}
                <Link 
                  to={`/signup?portal=${portal}`} 
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Sign up
                </Link>
              </p>

              {/* Portal Switcher */}
              {portal === 'patient' ? (
                <p className="text-xs text-muted-foreground text-center">
                  Are you a doctor?{' '}
                  <Link to="/doctor/login" className="text-primary hover:underline font-medium">
                    Doctor sign in
                  </Link>
                  {' '}·{' '}
                  <Link to="/doctor/signup" className="text-primary hover:underline font-medium">
                    Apply for access
                  </Link>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground text-center">
                  Not a {portal}?{' '}
                  <Link to="/login" className="text-primary hover:underline">
                    Patient login
                  </Link>
                </p>
              )}
            </CardFooter>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </motion.div>
    </div>
  );
}
